import React, { useEffect, useMemo, useState } from "react";
import { toast } from "react-toastify";
import { useTranslation } from "react-i18next";
import { isValidPhoneFormat } from "../../utils/phone";
import { FileText, CalendarClock, Truck, StickyNote, Trash, Pencil, Lock } from "lucide-react";
import { Modal } from "../UI/Modal";
import { SectionCard } from "../UI/SectionCard";
import Button from "../UI/Button";
import { useAppDispatch, useAppSelector } from "../../hooks/useStore";
import { Task } from "../../types/tasks";
import {
  createTask,
  deleteTask,
  updateTask,
} from "../../store/slices/tasksSlice";
import { withVersion } from "../../utils/concurrency";
import { taskAPI } from "../../services/api";
import { TaskFormGeneral } from "./TaskFormGeneral";
import { TaskFormRemarks } from "./TaskFormRemarks";
import { ActivityList } from "./ActivityList";
import { TaskFormTransport } from "./TaskFormTransport";
import { TransportDetails } from "../../types/tasks";
import { CreateShipmentButton, ShipmentValidationErrors } from "./CreateShipmentButton";
import { ShipmentBadge } from "./ShipmentBadge";

interface Props {
  isOpen: boolean;
  onClose: () => void;
  task?: Task | null;
}

const emptyTask: Partial<Task> = {
  type: "todo",
  description: "",
  status: "new",
  useStructuredForm: false,
  activities: [],
  transport: { cargoLines: [] },
};

export const TaskForm: React.FC<Props> = ({ isOpen, onClose, task }) => {
  const { t } = useTranslation("tasks");
  const dispatch = useAppDispatch();
  const { loading } = useAppSelector((state) => state.tasks);
  const authUser = useAppSelector((state) => state.auth.user);
  const isAdmin = authUser?.role === "admin";
  const isEditMode = !!task;

  const [draft, setDraft] = useState<Partial<Task>>(emptyTask);
  const [initialDraft, setInitialDraft] = useState<Partial<Task>>(emptyTask);
  const [errors, setErrors] = useState<Partial<Record<keyof Task, string>>>({});
  const [shipmentErrors, setShipmentErrors] = useState<ShipmentValidationErrors>({});
  const [isReadonly, setIsReadonly] = useState(false);
  const [linkedShipmentNumber, setLinkedShipmentNumber] = useState<string | undefined>(undefined);

  useEffect(() => {
    if (!isOpen) return;
    let base: Partial<Task> = task
      ? { ...task }
      : { ...emptyTask, transport: { cargoLines: [] } };

    // Non-admin creating a new task: pre-fill themselves as assignee
    if (!task && !isAdmin && authUser) {
      base = {
        ...base,
        assigneeId: authUser._id,
        assigneeName: `${authUser.firstName} ${authUser.lastName}`.trim(),
      };
    }

    setDraft(base);
    setInitialDraft(base);
    setErrors({});
    setIsReadonly(!!task); // open existing tasks in readonly, new tasks in edit mode
    setLinkedShipmentNumber(task?.linkedShipmentNumber ?? undefined);

    taskAPI.getAssignees()
      .then((r) => {
        // assignees fetched but used inside TaskFormGeneral via its own fetch
        void r;
      })
      .catch(() => {});
  }, [isOpen, task]);

  // Derived flags — always based on the persisted task, not draft status
  const isDone = task?.status === "done";
  const isLinked = !!(task?.linkedShipmentId || draft.linkedShipmentId);

  const isDirty = useMemo(
    () => JSON.stringify(draft) !== JSON.stringify(initialDraft),
    [draft, initialDraft],
  );

  const patch = (p: Partial<Task>) => {
    setDraft((prev) => ({ ...prev, ...p }));
    setShipmentErrors({});
  };

  const validate = (): boolean => {
    const next: Partial<Record<keyof Task, string>> = {};
    if (!draft.type) next.type = t("form.validation.typeRequired");
    if (!draft.description?.trim())
      next.description = t("form.validation.descriptionRequired");
    if (!draft.assigneeId)
      next.assigneeId = t("form.validation.assigneeRequired");
    setErrors(next);

    const badPhone = [draft.transport?.pickupPhone, draft.transport?.deliveryPhone].some(
      (value) => !isValidPhoneFormat(value),
    );
    if (badPhone) toast.error(t("form.validation.phoneInvalid"));

    return Object.keys(next).length === 0 && !badPhone;
  };

  const handleClose = () => {
    if (!isReadonly && isDirty && !window.confirm(t("form.confirmCancelDirty"))) return;
    onClose();
  };

  const handleCancelEdit = () => {
    setDraft(initialDraft);
    setErrors({});
    setIsReadonly(true);
  };

  const handleSave = async () => {
    if (!validate()) return;
    try {
      if (isEditMode && task) {
        // Explicitly send null for clientId when user cleared it so the backend unsets it.
        // JSON.stringify omits undefined, so without this the field would silently persist.
        const data: Record<string, unknown> = { ...draft };
        if (!draft.clientId) data.clientId = null;
        await dispatch(
          updateTask({
            id: task._id,
            data: withVersion(data, task) as Partial<Task>,
          }),
        ).unwrap();
        toast.success(t("messages.updatedSuccess"));
      } else {
        await dispatch(createTask(draft)).unwrap();
        toast.success(t("messages.createdSuccess"));
      }
      onClose();
    } catch {
      // error handled via slice
    }
  };

  const handleDelete = async () => {
    if (!task) return;
    if (!window.confirm(t("form.confirmDelete"))) return;
    try {
      await dispatch(deleteTask(task._id)).unwrap();
      toast.success(t("messages.deletedSuccess"));
      onClose();
    } catch {
      // error handled via slice
    }
  };

  const title = isEditMode
    ? `${task?.taskNumber ?? (task?._id ?? "").slice(-6)}`
    : t("module.createTask");

  const generalReadonly = isReadonly;
  const transportReadonly = isReadonly;
  const transportLocked = !isReadonly && isLinked; // edit mode but frozen fields
  const activitiesReadonly = isReadonly;
  const remarksReadonly = isReadonly;
  const assigneeLocked = !isAdmin;
  const canDelete = !isDone && !isReadonly && (isAdmin || task?.createdById === authUser?._id);

  return (
    <Modal isOpen={isOpen} onClose={handleClose} title={title} size="xl">
      <div className="flex-1 overflow-y-auto bg-gray-50">
        <div className="p-3 space-y-2">
          {/* Shipment link badge */}
          {isEditMode && draft.linkedShipmentId && (
            <ShipmentBadge
              shipmentId={draft.linkedShipmentId}
              shipmentNumber={linkedShipmentNumber}
            />
          )}

          {/* Done banner */}
          {isDone && (
            <div className="flex items-center gap-2 px-3 py-2 rounded-lg bg-gray-100 border border-gray-200 text-xs text-gray-600">
              <Lock className="w-3.5 h-3.5 shrink-0" />
              {t("form.doneBanner")}
            </div>
          )}

          {/* Linked shipment banner (only in edit mode) */}
          {!isDone && isLinked && (
            <div className="flex items-center gap-2 px-3 py-2 rounded-lg bg-amber-50 border border-amber-200 text-xs text-amber-700">
              <Lock className="w-3.5 h-3.5 shrink-0" />
              {t("form.linkedBanner")}
            </div>
          )}

          <SectionCard icon={FileText} title={t("form.sections.general")}>
            <TaskFormGeneral
              value={draft}
              onChange={patch}
              errors={{ ...errors, ...shipmentErrors }}
              readonly={generalReadonly}
              clientLocked={isLinked}
              assigneeLocked={assigneeLocked}
            />
          </SectionCard>

          <SectionCard icon={CalendarClock} title={t("form.sections.activities")}>
            <ActivityList
              taskId={task?._id ?? "new"}
              activities={draft.activities ?? []}
              onChange={(activities) => patch({ activities })}
              readonly={activitiesReadonly}
            />
          </SectionCard>

          {draft.useStructuredForm && (
            <SectionCard icon={Truck} title={t("form.sections.transport")}>
              <TaskFormTransport
                value={draft.transport ?? { cargoLines: [] }}
                onChange={(transportPatch: Partial<TransportDetails>) =>
                  patch({
                    transport: {
                      cargoLines: [],
                      ...(draft.transport ?? {}),
                      ...transportPatch,
                    },
                  })
                }
                readonly={transportReadonly}
                locked={transportLocked}
                errors={shipmentErrors}
              />
            </SectionCard>
          )}

          <SectionCard icon={StickyNote} title={t("form.sections.remarks")}>
            <TaskFormRemarks
              value={draft.taskRemarks ?? ""}
              onChange={(taskRemarks) => patch({ taskRemarks })}
              readonly={remarksReadonly}
            />
          </SectionCard>
        </div>
      </div>

      <div className="flex justify-between items-start gap-2 px-4 py-2.5 border-t border-gray-200 bg-white shrink-0">
        {/* Left: delete — edit mode only, not done, not readonly, role-gated */}
        <div className="shrink-0">
          {isEditMode && canDelete && (
            <Button
              type="button"
              variant="danger"
              onClick={handleDelete}
              disabled={loading}
            >
              <Trash className="h-4 w-4" />
            </Button>
          )}
        </div>

        {/* Right: action buttons — flex-wrap so they stack on narrow mobile */}
        <div className="flex items-center gap-2 flex-wrap justify-end">
          {isDone ? (
            // Completed task — close only
            <Button type="button" variant="outline" onClick={onClose}>
              {t("form.close")}
            </Button>
          ) : isReadonly ? (
            // View mode — Edit + Close
            <>
              <Button type="button" variant="outline" onClick={onClose}>
                {t("form.close")}
              </Button>
              <Button
                type="button"
                variant="primary"
                onClick={() => setIsReadonly(false)}
                icon={<Pencil className="h-4 w-4" />}
              >
                {t("form.edit")}
              </Button>
            </>
          ) : (
            // Edit mode — Cancel (revert to view) + Create Shipment + Save
            <>
              <CreateShipmentButton
                task={task ?? null}
                draft={draft}
                onCreated={(shipmentId, shipmentNumber) => {
                  patch({ linkedShipmentId: shipmentId });
                  setInitialDraft((prev) => ({ ...prev, linkedShipmentId: shipmentId }));
                  setLinkedShipmentNumber(shipmentNumber);
                }}
                onValidationErrors={setShipmentErrors}
                disabled={loading}
              />
              <Button type="button" variant="outline" onClick={isEditMode ? handleCancelEdit : onClose}>
                {t("form.cancel")}
              </Button>
              <Button
                type="button"
                variant="primary"
                onClick={handleSave}
                isLoading={loading}
                disabled={loading}
              >
                {t("form.save")}
              </Button>
            </>
          )}
        </div>
      </div>
    </Modal>
  );
};
